<template>
  <v-autocomplete
    prepend-inner-icon="search"
    dark
    append-icon
    placeholder="Search"
    :search-input.sync="search"
    :loading="isLoading"
    outlined
    dense
    no-data-text="No Results"
    clearable
    open-on-clear
    :items="items"
    :no-filter="true"
    style="height: 3.125rem"
    data-e2e-id="global-search"
  >
    <template #item="{ item }">
      <v-list-item v-if="item.type === 'header'">
        <v-row no-gutters class="text-no-wrap">
          <v-col>
            <v-subheader class="px-0">{{ item.text }}</v-subheader>
          </v-col>
          <v-spacer />
          <v-col v-if="item.seeMore">
            <v-subheader
              class="viper-link primary--text px-0"
              style="cursor: pointer"
              @click="seeMoreResults(item.value)"
              >SEE MORE RESULTS</v-subheader
            >
          </v-col>
        </v-row>
      </v-list-item>
      <v-list-item v-else-if="item.type === 'patient'" @click="clickPatient(item.id)">
        <v-layout column class="pa-2" :data-e2e-id="'result-' + item.externalId">
          <v-flex>{{ item.firstName }} {{ item.lastName }}</v-flex>
          <v-flex class="font-weight-bold">{{ item.externalId }}</v-flex>
        </v-layout>
      </v-list-item>
      <v-list-item v-else-if="item.type === 'trial'" @click="clickTrial(item.trialId)">
        <v-layout column class="pa-2">
          <v-flex>{{ item.externalId }}</v-flex>
          <v-flex class="font-weight-bold">{{ item.trialExternalId }}</v-flex>
        </v-layout>
      </v-list-item>
    </template>
    <template #no-data>
      <div v-if="isLoading">
        <v-progress-circular
          class="center"
          indeterminate
          color="primary"
          data-e2e-id="global-search-loading"
        />
      </div>
      <div v-else-if="search" class="text-subtitle-2 grey--text">
        <div class="font-weight-bold text-subtitle-1 black--text px-3">No Results</div>
      </div>
      <div v-else class="text-center text-subtitle-2 grey--text" style="width: 20rem">
        <div class="font-weight-bold text-subtitle-1 black--text">What are you looking for?</div>
        <div>Search by Patient Name, Patient ID, Trial ID, or Trial Name</div>
      </div>
    </template>
  </v-autocomplete>
</template>

<script>
import { MyActiveOrganization } from '@/helpers/my-account-helper'
import { getTrialsHomeLink } from '@/helpers/router-helper'
import Vue from 'vue'
import { mapActions } from 'vuex'
import _ from 'lodash'
import { isFeatureOn } from '@/helpers/feature-flag-helper'

export default {
  props: {
    results: { type: Object, default: () => {} }
  },
  data() {
    return {
      items: [],
      search: null,
      orgId: MyActiveOrganization.id,
      isLoading: false
    }
  },
  watch: {
    search() {
      this.isLoading = true
      this.searchWatch()
    },
    results() {
      const patients = _.chain(this.results)
        .get('patients', [])
        .map(it => {
          Vue.set(it, 'type', 'patient')
          return it
        })
        .value()
      const trials = _.chain(this.results)
        .get('trials', [])
        .map(it => {
          Vue.set(it, 'type', 'trial')
          return it
        })
        .value()
      this.items = this.generateItemsList(patients, trials)
    }
  },
  methods: {
    ...mapActions(['REFRESH_PATIENT_SEARCH']),
    searchWatch: _.debounce(function() {
      this.$emit('searchChanged', this.search)
    }, 500),
    generateItemsList(patients, trials) {
      let listItems = []
      if (patients.length === 0 && trials.length === 0) {
        listItems = []
      } else if (patients.length === 0 && trials.length > 0) {
        listItems = [
          ...patients,
          {
            text: 'TRIALS',
            value: 'trials',
            disabled: false,
            divider: false,
            header: '',
            type: 'header',
            seeMore: false
          },
          ...trials
        ]
      } else if (patients.length > 0 && trials.length === 0) {
        listItems = [
          {
            text: 'PATIENTS',
            value: 'patients',
            disabled: false,
            divider: false,
            header: '',
            type: 'header',
            seeMore: patients.length >= 5
          },
          ...patients,
          ...trials
        ]
      } else if (patients.length > 0 && trials.length > 0) {
        listItems = [
          {
            text: 'PATIENTS',
            value: 'patients',
            disabled: false,
            divider: false,
            header: '',
            type: 'header',
            seeMore: patients.length >= 5
          },
          ...patients,
          {
            text: 'TRIALS',
            value: 'trials',
            disabled: false,
            divider: false,
            header: '',
            type: 'header',
            seeMore: false
          },
          ...trials
        ]
      }

      this.isLoading = false
      return listItems
    },
    clickPatient(patientId) {
      this.search = null
      this.$emit('searchChanged', this.search)
      if (isFeatureOn('viper-platform-ui-react-patient-details-page')) {
        window.location.href = `${window.$appConfig.VUE_APP_PLATFORM_UI_URL}/org/${this.orgId}/patient-details/${patientId}`
        return
      }
      this.$store.dispatch('GET_PATIENT', { patientId: patientId })
      this.$router.push({
        name: 'patient-demographics',
        params: { orgId: this.orgId, patientId: patientId }
      })
    },
    clickTrial(trialId) {
      this.search = null
      this.$emit('searchChanged', this.search)
      if (isFeatureOn('viper-platform-ui-react-trials-page')) {
        window.location.href = `${window.$appConfig.VUE_APP_PLATFORM_UI_URL}/org/${this.orgId}/trials?selectedTrial=${trialId}`
        return
      }
      this.$store.dispatch('GET_TRIAL', {
        orgId: this.orgId,
        trialId: this.trialId
      })
      this.$router.push(getTrialsHomeLink(trialId))
    },
    seeMoreResults(type) {
      const searchString = this.search
      this.search = null

      if (type === 'patients') {
        if (this.$route.name === 'patient-search-list') {
          this.REFRESH_PATIENT_SEARCH(searchString)
        } else {
          this.$router.push({
            name: 'patient-search-list',
            params: { orgId: this.orgId },
            query: {
              searchString: searchString,
              pageNumber: 1,
              itemsPerPage: 10,
              sortField: 'sortableName',
              sortDirection: 'desc'
            }
          })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.center {
  display: block;
  margin: auto;
}
</style>
